import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SidebarSection, { SIDEBAR_SECTION_ITEM_CLASSES } from '../Base/components/SidebarSection';
import { getProjects } from '../../services/ProjectService';
import { useDiffitUserContext } from '../../contexts/DiffitUserContext';

/**
 * Renders a collapsible "Recent" resources section into the AppSidebar portal slot.
 * Used on homepage (via UnlimitedSidebar), packet pages, and project pages.
 */
export const RecentResourcesSidebar = () => {
  // Gate on the Diffit user (not Clerk's isSignedIn): on a fresh signup the
  // backend user row doesn't exist until POST /user completes, so an earlier
  // GET /project would 404 with "User not found".
  const { diffitUser } = useDiffitUserContext();
  const diffitUserId = diffitUser?.id;
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMoreProjects, setHasMoreProjects] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getProjects(1, 5, '', false);
      const newProjects = response.data.results || [];
      const totalCount = response.data.pagination?.total || 0;
      setHasMoreProjects(totalCount > 5);
      setProjects(newProjects);
    } catch (error) {
      console.error('Error fetching recent projects:', error);
      setProjects([]);
      setHasMoreProjects(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!diffitUserId) {
      setIsLoading(false);
      setProjects([]);
      setHasMoreProjects(false);
      return;
    }
    fetchProjects();
  }, [fetchProjects, diffitUserId]);

  if (!diffitUserId) return null;
  if (!isLoading && projects.length === 0) return null;

  return (
    <SidebarSection title='Recent' open={open} setOpen={setOpen}>
      {isLoading ? (
        <div className='px-4 py-4 text-center text-sm text-gray-600'>Loading...</div>
      ) : (
        <ul>
          {projects.slice(0, 5).map((project) => {
            const to =
              project.type === 'legacy_packet' ? `/p/${project.id}` : `/project/${project.id}`;
            return (
              <li key={project.id} className='mx-2 group'>
                <Link to={to} className={SIDEBAR_SECTION_ITEM_CLASSES}>
                  {project.title || 'Untitled'}
                </Link>
              </li>
            );
          })}
          {hasMoreProjects && (
            <li className='mx-2'>
              <Link to='/resources' className={SIDEBAR_SECTION_ITEM_CLASSES}>
                See all
              </Link>
            </li>
          )}
        </ul>
      )}
    </SidebarSection>
  );
};
